import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import dataGif from "../img/product/data1.webm";
import chartGif from "../img/product/chart1.webm";
import reportGif from "../img/product/report.webm";
import "../styles/labeling.scss";
import { getCanonicalUrl } from "../utils/seoConfig";

function AnomalyDetection() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const featureRefs = useRef([]);

  useEffect(() => {
    window.scrollTo(0, 0);

    // Intersection Observer za animacije
    const observerOptions = {
      threshold: 0.1,
      rootMargin: "0px 0px -50px 0px",
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("visible");
        }
      });
    }, observerOptions);

    const currentRefs = featureRefs.current;
    currentRefs.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => {
      currentRefs.forEach((ref) => {
        if (ref) observer.unobserve(ref);
      });
    };
  }, []);

  const features = [
    {
      id: "upload-data",
      video: dataGif,
      title: t("UploadYourData"),
      description: t("CSVFormatDescription"),
      tag: t("DataFormat"),
      reverse: false,
      metric: {
        value: "CSV",
        label: t("SupportedFormat"),
      },
    },
    {
      id: "anomaly-detection",
      video: chartGif,
      title: t("AutomatedFaultDetection"),
      description: t("AutomatedFaultDetectionDesc"),
      tag: t("RealTimeMonitoring"),
      reverse: true,
      metric: {
        value: "10+",
        label: t("MachineLearningModels"),
      },
    },
    {
      id: "business-report",
      video: reportGif,
      title: t("BusinessIntelligenceReport"),
      description: t("BusinessIntelligenceReportDesc"),
      tag: t("SmartAnalytics"),
      reverse: false,
      metric: {
        value: "< 24h",
        label: t("FirstInsights"),
      },
    },
  ];

  return (
    <>
      <Helmet>
        <title>{t("AnomalyDetectionPageTitle")}</title>
        <meta
          name="description"
          content={t("AnomalyDetectionPageDescription")}
        />
        <meta name="keywords" content={t("AnomalyDetectionPageKeywords")} />
        <link
          rel="canonical"
          href={getCanonicalUrl("/products/anomaly-detection")}
        />
      </Helmet>
      <div className="labeling-page">
        <div className="labeling-header">
          <div className="labeling-header-content">
            <span className="product-tag b2b-only">{t("B2BOnly")}</span>
            <h1 className="labeling-header-title">{t("AnomalyDetection")}</h1>
            <p className="labeling-header-description">
              {t("AnomalyDetectionDescription")}
            </p>
            <div className="product-features">
              <div className="product-feature">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M5 13L9 17L19 7"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span>{t("AutomatedAnomalyDetection")}</span>
              </div>
              <div className="product-feature">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M5 13L9 17L19 7"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span>{t("ThresholdEventLabeling")}</span>
              </div>
              <div className="product-feature">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M5 13L9 17L19 7"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span>{t("SelfServiceEnterprise")}</span>
              </div>
            </div>
            <div className="product-cta">
              <a
                href="https://app.predsense.ai/"
                target="_blank"
                rel="noopener noreferrer"
                className="primary-button"
              >
                {t("LoginToApp")}
              </a>
              <button
                className="secondary-button"
                onClick={() => navigate("/signup")}
              >
                {t("ContactForB2B")}
              </button>
            </div>
          </div>
        </div>

        <div className="labeling-features">
          {features.map((feature, index) => (
            <div
              key={feature.id}
              ref={(el) => (featureRefs.current[index] = el)}
              className={`labeling-feature-item ${
                feature.reverse ? "reverse" : ""
              }`}
            >
              <div className="labeling-feature-content">
                <span className="labeling-feature-tag">{feature.tag}</span>
                <h2 className="labeling-feature-title">{feature.title}</h2>
                <p className="labeling-feature-description">
                  {feature.description}
                </p>
                {feature.metric && (
                  <div className="feature-metrics single">
                    <div className="metric">
                      <span className="metric-value">
                        {feature.metric.value}
                      </span>
                      <span className="metric-label">
                        {feature.metric.label}
                      </span>
                    </div>
                  </div>
                )}
              </div>
              <div className="labeling-feature-video-wrapper">
                <video
                  autoPlay
                  loop
                  muted
                  playsInline
                  className="labeling-feature-video"
                  aria-label={feature.title}
                >
                  <source src={feature.video} type="video/webm" />
                </video>
                <div className="labeling-video-overlay"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

export default AnomalyDetection;
